import { cn } from "../lib/cn";
import { findLibraryImage } from "../lib/imageLibrary";

/**
 * One image of the bundled library, sized by its box's height: a pictogram
 * (icon) is a square drawn in the current text color, a scope map in the light
 * `--library-map` tone (or the current color with `inheritColor`), artwork
 * (logo, flag) keeps its own colors and proportions. Unknown codes render nothing.
 */
export function LibraryImage({
  code,
  className,
  decorative = false,
  inheritColor = false,
  mapColor = "var(--library-map)",
}: {
  code: string;
  className?: string;
  /** Next to text that already says the same: hidden from assistive technology. */
  decorative?: boolean;
  /** A map takes the surrounding color (a page accent) instead of the map tone. */
  inheritColor?: boolean;
  /** The tone of a map when it does not inherit the color (default `--library-map`). */
  mapColor?: string;
}) {
  const image = findLibraryImage(code);
  if (!image) return null;
  const name = decorative ? { "aria-hidden": true } : { role: "img", "aria-label": image.label };
  if (image.tint) {
    return (
      <span
        {...name}
        title={decorative ? undefined : image.label}
        data-library-image={image.code}
        className={cn("inline-block aspect-square h-10 shrink-0 bg-current", className)}
        style={{
          ...(image.category === "map" && !inheritColor ? { color: mapColor } : {}),
          maskImage: `url("${image.url}")`,
          maskSize: "contain",
          maskPosition: "center",
          maskRepeat: "no-repeat",
        }}
      />
    );
  }
  return (
    <img
      src={image.url}
      alt={decorative ? "" : image.label}
      draggable={false}
      data-library-image={image.code}
      className={cn("block h-10 w-auto max-w-full shrink-0 object-contain select-none", className)}
    />
  );
}
